import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { KibButtonNew } from '@chewy/kib-controls-react'
import { KibSpinner } from '@chewy/kib-interstitials-react'
import { Utils } from '@pbt/pbt-ui-components'

import { ContextualBackButton } from '~/components/common/buttons/ContextualBackButton'
import { RightRailOverlayProvider } from '~/components/dashboard/soapV2/context/RightRailOverlayContext'
import { PatientMedicalSummaryRail } from '~/components/dashboard/soapV2/summary/PatientMedicalSummaryRail'
import DialogNames from '~/constants/DialogNames'
import FeatureToggle from '~/constants/featureToggle'
import { PrescriptionStates } from '~/constants/prescription'
import { OrderType } from '~/constants/SOAPStates'
import {
  getFeatureToggle,
  getPrescriptionStates,
} from '~/store/reducers/constants'
import { getPrescriptionWorkflowType } from '~/utils/prescription'
import useDialog from '~/utils/useDialog'

import ClientAndPatientSection from './details/new-client-and-patient/ClientAndPatientSection'
import { BaseRxCard } from './timeline/BaseRxCard'
import {
  isDrugSubtype,
  usePrescriptionDetailsContent,
} from './usePrescriptionDetailsContent'

import '@chewy/kib-interstitials-styles/dist/styles/kib-spinner.css'
import '@chewy/kib-controls-styles/dist/styles/kib-button-new.css'
import styles from './PrescriptionDetailsPage.module.scss'

const PrescriptionDetailsPage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation('Common')
  const { itemId } = useParams()
  const [searchParams] = useSearchParams()
  const [openCancelRxModal] = useDialog(DialogNames.CANCEL_RX_MODAL)
  const isCancelPrescriptionEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CANCEL_RX),
  )
  const prescriptionStates = useSelector(getPrescriptionStates)

  const mode = isDrugSubtype(searchParams.get('subtype'))
    ? 'orderedDrug'
    : 'prescription'

  const { item, clientId, patientId, isLoading, hasData } =
    usePrescriptionDetailsContent(itemId, mode)

  const prescription = item?.prescription
  const prescriptionStateName = isCancelPrescriptionEnabled
    ? Utils.getConstantName(prescription?.stateId, prescriptionStates)
    : undefined

  const { isChewyActiveRx, isChewyReactiveRx } = isCancelPrescriptionEnabled
    ? getPrescriptionWorkflowType({
        orderType: prescription?.type ?? OrderType.PRESCRIPTION,
        prescriptionType: prescription?.prescriptionType,
        origin: prescription?.origin,
      })
    : { isChewyActiveRx: false, isChewyReactiveRx: false }

  const isAlreadyCanceled =
    prescriptionStateName === PrescriptionStates.CANCELED ||
    prescriptionStateName === PrescriptionStates.DECLINED
  const isVetApproved = prescriptionStateName === PrescriptionStates.APPROVED

  const shouldShowCancelPrescription =
    isCancelPrescriptionEnabled &&
    Boolean(prescription?.id) &&
    isVetApproved &&
    (isChewyActiveRx || isChewyReactiveRx) &&
    !isAlreadyCanceled

  const handleCancelPrescription = () => {
    if (!isCancelPrescriptionEnabled || !prescription?.id) return
    openCancelRxModal()
  }

  const handleBackNavigation = () => {
    if (clientId && patientId) {
      navigate(`/client/${clientId}/patient/${patientId}`)
    } else if (clientId) {
      navigate(`/client/${clientId}`)
    } else {
      navigate(-1)
    }
  }

  return (
    <div className={styles.root}>
      {clientId && (
        <div className={styles.clientSection}>
          <ClientAndPatientSection
            clientId={clientId}
            patientId={patientId ?? undefined}
          />
        </div>
      )}
      <div className={styles.mainContent}>
        <ContextualBackButton
          buttonText={t('Common:CLIENT_AND_PATIENT_DETAIL')}
          navigateBackToContext={handleBackNavigation}
        />
        <div className={styles.content}>
          <div className={styles.header}>
            {isLoading && (
              <div className={styles.loading}>
                <KibSpinner />
              </div>
            )}
            {!isLoading && hasData && item?.prescription && (
              <BaseRxCard
                isDetailView
                className={styles.rxDetailsSpacing}
                headerAction={
                  shouldShowCancelPrescription ? (
                    <KibButtonNew
                      emphasis="secondary"
                      size="small"
                      theme="destructive"
                      type="button"
                      onClick={handleCancelPrescription}
                    >
                      {t('CANCEL_PRESCRIPTION')}
                    </KibButtonNew>
                  ) : undefined
                }
                item={item}
              />
            )}
          </div>
        </div>
      </div>
      {clientId && patientId && (
        <div className={styles.rightRail}>
          <RightRailOverlayProvider>
            <PatientMedicalSummaryRail
              clientId={clientId}
              patientId={patientId}
            />
          </RightRailOverlayProvider>
        </div>
      )}
    </div>
  )
}

export { PrescriptionDetailsPage }
